import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  TextField,
  Typography
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useGetOne, useTranslate } from 'react-admin';
import { useParams } from 'react-router-dom';
import { BackButton } from '../../../components/shared/BackButton';
import { Chart, ChartSerieType } from '../../../components/shared/Chart';
import { PUMPS_INDEX_URL } from '../../../core/constants/url.constants';
import { isNullOrUndefined } from '../../../core/utils/object-utils';
import { PumpCurvesDownloadPdfButton } from './PumpCurvesDownloadPdfButton';

const SAFEM_COLOR = '#006072';

interface PumpLine {
  vacuum: number;
  capacityActual: number;
  powerAbsorbed: number;
}

interface PumpSpeed {
  id: string;
  speed: number;
  pumpLines: PumpLine[];
}

export const PumpCurvesShow = () => {
  const params = useParams();
  const t = useTranslate();
  const [selectedSpeedIds, setSelectedSpeedIds] = useState<string[]>([]);

  const { data: pumpData, isLoading: loading } = useGetOne('pumps', {
    id: params.pumpId || ''
  });

  const speedsWithValidData = useMemo(
    () =>
      ((pumpData?.pumpSpeeds ?? []) as PumpSpeed[])
        .filter((s) => s.pumpLines && s.pumpLines.length > 0)
        .sort((a, b) => a.speed - b.speed),
    [pumpData]
  );

  useEffect(() => {
    if (speedsWithValidData.length > 0) {
      setSelectedSpeedIds((prev) =>
        prev.length === 0 ? speedsWithValidData.map((s) => s.id) : prev
      );
    }
  }, [speedsWithValidData]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  const hasData = speedsWithValidData.length > 0;
  const pumpName = pumpData?.pumpName || '';

  const speedOptions = speedsWithValidData.map((s) => ({
    id: s.id,
    label: `${s.speed} RPM`
  }));

  const filteredSpeeds = speedsWithValidData.filter((s) =>
    selectedSpeedIds.includes(s.id)
  );

  const capacitySeries: ChartSerieType[] = filteredSpeeds.map((pumpSpeed) => ({
    name: `${pumpSpeed.speed} RPM`,
    color: SAFEM_COLOR,
    data: pumpSpeed.pumpLines
      .filter(
        (line) =>
          !isNullOrUndefined(line.vacuum) &&
          !isNullOrUndefined(line.capacityActual) &&
          !isNaN(line.vacuum) &&
          !isNaN(line.capacityActual)
      )
      .map((line) => ({
        x: line.vacuum,
        y: line.capacityActual
      }))
  }));

  const powerSeries: ChartSerieType[] = filteredSpeeds.map((pumpSpeed) => ({
    name: `${pumpSpeed.speed} RPM`,
    color: SAFEM_COLOR,
    data: pumpSpeed.pumpLines
      .filter(
        (line) =>
          !isNullOrUndefined(line.vacuum) &&
          !isNullOrUndefined(line.powerAbsorbed) &&
          !isNaN(line.vacuum) &&
          !isNaN(line.powerAbsorbed)
      )
      .map((line) => ({
        x: line.vacuum,
        y: line.powerAbsorbed
      }))
  }));

  return (
    <Box className="pump-curves-container" sx={{ padding: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: 3
        }}
      >
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <BackButton to={PUMPS_INDEX_URL} />
          <Typography variant="h5" component="h1">
            {t('resources.pumps.curves.title')}: {pumpName}
          </Typography>
        </Box>
        {hasData && (
          <Autocomplete
            multiple
            disableCloseOnSelect
            size="small"
            options={speedOptions}
            value={speedOptions.filter((o) => selectedSpeedIds.includes(o.id))}
            onChange={(_, newValue) =>
              setSelectedSpeedIds(newValue.map((v) => v.id))
            }
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
                {option.label}
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.length <= 6
                ? value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.label}
                      size="small"
                    />
                  ))
                : [
                    <Chip
                      key="summary"
                      label={t('resources.pumps.curves.selectedSpeeds', {
                        count: value.length
                      })}
                      size="small"
                    />
                  ]
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('resources.pumps.curves.selectSpeeds')}
              />
            )}
            sx={{ width: '30%', flexShrink: 0 }}
          />
        )}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <PumpCurvesDownloadPdfButton
            pumpId={params.pumpId || ''}
            pumpName={pumpName}
            disabled={!hasData || selectedSpeedIds.length === 0}
            speedIds={selectedSpeedIds}
          />
        </Box>
      </Box>

      {hasData ? (
        <>
          <Box sx={{ marginBottom: 4 }}>
            <Chart
              title={t('resources.pumps.curves.capacityCurve')}
              xTitle="kPa"
              yTitle="m³/min"
              series={capacitySeries}
              showLineLabels={true}
              hideLegend={true}
              fixedDomain={{ x: [0, 100] }}
            />
          </Box>
          <Box sx={{ marginBottom: 4 }}>
            <Chart
              title={t('resources.pumps.curves.powerCurve')}
              xTitle="kPa"
              yTitle="kW"
              series={powerSeries}
              showLineLabels={true}
              hideLegend={true}
              fixedDomain={{ x: [0, 100] }}
            />
          </Box>
        </>
      ) : (
        <Typography variant="body1" color="textSecondary">
          {t('resources.pumps.curves.noData')}
        </Typography>
      )}
    </Box>
  );
};
